import React, { useCallback, useEffect, useRef, useState, FC } from 'react';
import Slider from 'react-slick';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { documentService } from '../../utils/window.utils';
import { BlogNewsModel } from './models/blognews.model';
import { TBlogNewsProps } from './types/blogNews.types';
import Heading from '../@shared/heading';
import CarouselArrow from '../@shared/carouselArrow';
import StoriesCard from './components/storiesCard';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { googleClickCtaEvent } from '../../utils/analytics';
import UnreleasedComponent from '../@shared/unreleasedComponent';

type TArrowDomObj = {
  leftArrow: HTMLButtonElement | null;
  rightArrow: HTMLButtonElement | null;
};

const BlogNews: FC<TBlogNewsProps> = (props) => {
  const model = new BlogNewsModel(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`, true);
  const [arrowDomObj, setArrowDomObj] = useState<TArrowDomObj>({ leftArrow: null, rightArrow: null });
  const cardWidthRef = useRef<HTMLDivElement | null>(null);

  const findSlidesToShow = useCallback((): number => {
    const DESKTOP_CARD_WIDTH = 200;
    const DESKTOP_CARD_MARGIN = 16;
    const MOBILE_CARD_WIDTH = 163;
    const MOBILE_CARD_MARGIN = 8;
    const cardContainerWidth = cardWidthRef?.current?.clientWidth || 1680;
    const cardWidth = isDesktop ? DESKTOP_CARD_WIDTH : MOBILE_CARD_WIDTH;
    const cardMargin = isDesktop ? DESKTOP_CARD_MARGIN : MOBILE_CARD_MARGIN;
    const slidesCanBeShown = Math.floor(cardContainerWidth / (cardWidth + cardMargin));

    return model.cards.length > slidesCanBeShown ? slidesCanBeShown || 7 : model.cards.length;
  }, [isDesktop, model.cards.length]);

  const storiesMapper = useCallback(
    (blogModel: BlogNewsModel) => {
      return blogModel.cards.map((item) => (
        <div className="blog-news__card-container" key={item?.fields?.title?.value}>
          <StoriesCard
            {...item}
            sitecoreContext={props.sitecoreContext}
            onClick={(): void =>
              googleClickCtaEvent(
                'Link',
                item.fields?.link?.value?.text ?? item.fields?.link?.value?.title,
                item.fields?.title?.value || ''
              )
            }
            cardHeadingTag={model?.cardHeadingTitleTag}
            isGradientDisabled={model?.isGradientDisabled}
            textColor={model?.textColor}
          />
        </div>
      ));
    },
    [model, props.sitecoreContext] //eslint-disable-line
  );

  const [slidesToShow, setSlidesToShow] = useState(4.5);
  useEffect(() => setSlidesToShow(findSlidesToShow()), [cardWidthRef, findSlidesToShow]);

  const settings = {
    slidesToShow: slidesToShow,
    arrows: true,
    infinite: false,
    variableWidth: true,
    slidesToScroll: 1,
    nextArrow: <CarouselArrow direction="next" />,
    prevArrow: <CarouselArrow direction="previous" />,
    afterChange: (index): void => {
      if (arrowDomObj.leftArrow?.style) {
        arrowDomObj.leftArrow.style.visibility = index === 0 ? 'hidden' : 'visible';
      }
      if (arrowDomObj.rightArrow?.style) {
        arrowDomObj.rightArrow.style.visibility = index === model.cards.length - slidesToShow ? 'hidden' : 'visible';
      }
    },
  };

  useEffect(() => {
    const leftArrow: HTMLButtonElement = documentService().querySelector('.blog-news .carousel-arrow--left');
    const rightArrow: HTMLButtonElement = documentService().querySelector('.blog-news .carousel-arrow--right');
    setArrowDomObj({ leftArrow, rightArrow });

    // hiding left arrow on load
    if (leftArrow?.style) {
      leftArrow.style.visibility = 'hidden';
    }
  }, []);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering} title={model.title?.value} template="Blog News">
      <div className="blog-news">
        <div className="blog-news__content" ref={cardWidthRef}>
          <div className="blog-news__text">
            <Heading
              eyebrow={model.eyebrow}
              title={model.title}
              subtitle={model.content}
              link={model.link}
              customTextTitle
              useAnalytics
              editable={true}
              tag={model.headingTag}
            />
          </div>
          <Slider {...settings}>{storiesMapper(model)}</Slider>
        </div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(BlogNews);
